/*
 * Project: bellcloud
 * File: packages/ui/components/common/device/DeviceStatusCard.tsx
 * Author: SWAN
 * Created: 2025-01-XX
 * Description: 디바이스 상태 카드 컴포넌트 - 테이블 형식으로 센서 정보 표시
 */

import React from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@/theme';
import { Tag } from '../Tag';
import { BATTERY_STATE_MAP } from '@packages/types/device/lite/batteryState';
import battery0Svg from '/icons/battery-0.svg?raw';
import battery1Svg from '/icons/battery-1.svg?raw';
import battery2Svg from '/icons/battery-2.svg?raw';
import battery3Svg from '/icons/battery-3.svg?raw';
import battery4Svg from '/icons/battery-4.svg?raw';
import thermometerSvg from '/icons/thermometer.svg?raw';
import iconStableSvg from '/icons/IconStable.svg?raw';
import iconWarningSvg from '/icons/IconWarning.svg?raw';
import iconAlertSvg from '/icons/IconAlert.svg?raw';
import { Flex } from '../Layout';
import { Card, CardTitle } from '../Card';

// ==================== Types ====================
export interface SensorStatusData {
  type: string;
  value: number | string | null;
  unit: string;
  status: 'Stable' | 'Warning' | 'Alert' | 'Disabled';
  statusLabel?: string;
  additionalInfo?: string;
}

interface DeviceStatusCardProps {
  deviceName: string;
  serialNumber: string;
  deviceType: string;
  timestamp: string;
  temperature?: string;
  batteryState?: number;
  deviceImageUrl?: string;
  sensors: SensorStatusData[];
  /** 온보딩 가이드 타겟용 data-tour 속성 */
  'data-tour'?: string;
}

// ==================== Styled Components ====================

const Timestamp = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${({ theme }) => theme.spacing[6]}px;
`;

const DeviceInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]}px;
  min-width: 120px;
  padding-right: ${({ theme }) => theme.spacing[4]}px;
  border-right: 1px solid ${({ theme }) => theme.colors.border.divider};
`;

const DeviceImageContainer = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
`;

const DeviceImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const DeviceInfo = styled.div`
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  word-break: break-word;
`;

const MetricsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]}px;
  align-items: center;
  margin-top: ${({ theme }) => theme.spacing[2]}px;
`;

const MetricItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${({ theme }) => theme.fontSize.xs}px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const SensorTableSection = styled.div`
  flex: 1;
  overflow-x: auto;
`;

const SensorTable = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: ${({ theme }) => theme.spacing[3]}px;
  width: 100%;
`;

const SensorColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]}px;
  border-right: 1px solid ${({ theme }) => theme.colors.border.divider};
  padding-right: ${({ theme }) => theme.spacing[3]}px;

  &:last-child {
    border-right: none;
  }
`;

const SensorHeader = styled.div`
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
  padding: ${({ theme }) => theme.spacing[2]}px 0;
  background-color: ${({ theme }) => theme.colors.surface.header.default};
`;

const SensorValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg}px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  text-align: center;
`;

const SensorUnit = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
`;

const SensorAdditionalInfo = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  align-items: center;
  word-break: break-word;
  line-height: ${({ theme }) => theme.lineHeight.normal};
`;

// ==================== Icon Components ====================
function BatteryIcon({ batteryState, size = 16 }: { batteryState: number; size?: number }) {
  // batteryState가 숫자면 직접 사용, 문자열이면 파싱

  let svgContent = '';
  if (batteryState === 1) {
    svgContent = battery0Svg;
  } else if (batteryState === 2) {
    svgContent = battery1Svg;
  } else if (batteryState === 3) {
    svgContent = battery2Svg;
  } else if (batteryState === 4) {
    svgContent = battery3Svg;
  } else if (batteryState === 5) {
    svgContent = battery4Svg;
  }

  if (batteryState === 0) {
    svgContent = svgContent.replace(/#21272A/g, '#ef4444');
  }

  svgContent = svgContent.replace(/width="24" height="24"/, `width="${size}" height="${size}"`);

  const uniqueId = `battery-${Math.random().toString(36).substr(2, 9)}`;
  const clipPathId = `clip0_${uniqueId}`;
  svgContent = svgContent.replace(/clip0_\d+/g, clipPathId);
  svgContent = svgContent.replace(/url\(#clip0_\d+\)/g, `url(#${clipPathId})`);

  return <IconWrapper dangerouslySetInnerHTML={{ __html: svgContent }} />;
}

function ThermometerIcon({ size = 16 }: { size?: number }) {
  let svgContent = thermometerSvg;
  svgContent = svgContent.replace(/width="16" height="16"/, `width="${size}" height="${size}"`);
  return <IconWrapper dangerouslySetInnerHTML={{ __html: svgContent }} />;
}

function StatusIcon({ status }: { status: 'Stable' | 'Warning' | 'Alert' | 'Disabled' }) {
  if (status === 'Disabled') return null;

  const svgMap = {
    Stable: iconStableSvg,
    Warning: iconWarningSvg,
    Alert: iconAlertSvg,
  };

  let svgContent = svgMap[status];
  svgContent = svgContent.replace(/width="12" height="12"/, 'width="16" height="16"');

  return <IconWrapper dangerouslySetInnerHTML={{ __html: svgContent }} />;
}

// ==================== Main Component ====================
export const DeviceStatusCard: React.FC<DeviceStatusCardProps> = ({
  deviceName: _deviceName,
  serialNumber,
  deviceType,
  timestamp,
  temperature,
  batteryState,
  deviceImageUrl,
  sensors,
  'data-tour': dataTour,
}) => {
  const { theme } = useTheme();

  return (
    <Card data-tour={dataTour}>
      <Flex direction="row" justify="between" align="center">
        <CardTitle>Device Status</CardTitle>
        <Timestamp>{timestamp.toLocaleString()}</Timestamp>
      </Flex>

      <ContentGrid>
        {/* Left: Device Info */}
        <DeviceInfoSection>
          {deviceImageUrl && (
            <DeviceImageContainer>
              <DeviceImage
                src={deviceImageUrl}
                alt={deviceType}
                onError={e => {
                  const img = e.currentTarget;
                  img.onerror = null;
                  img.style.visibility = 'hidden';
                }}
              />
            </DeviceImageContainer>
          )}
          <DeviceInfo>{serialNumber}</DeviceInfo>

          <MetricsContainer>
            {temperature && (
              <MetricItem>
                <ThermometerIcon size={14} />
                <span>{temperature}</span>
              </MetricItem>
            )}
            {batteryState !== undefined && batteryState !== null && (
              <MetricItem>
                {batteryState !== 0 && <BatteryIcon batteryState={batteryState} size={14} />}
                <span>
                  {typeof batteryState === 'number'
                    ? BATTERY_STATE_MAP[batteryState] || batteryState
                    : batteryState}
                </span>
              </MetricItem>
            )}
          </MetricsContainer>
        </DeviceInfoSection>

        {/* Right: Sensor Table */}
        <SensorTableSection>
          <SensorTable style={{ gridTemplateColumns: `repeat(${sensors.length}, 1fr)` }}>
            {sensors.map((sensor, idx) => {
              const isDisabled = sensor.status === 'Disabled';

              // status를 Tag의 variant로 매핑
              const tagVariant =
                sensor.status === 'Stable'
                  ? 'success'
                  : sensor.status === 'Warning'
                    ? 'warning'
                    : sensor.status === 'Alert'
                      ? 'error'
                      : 'custom';

              return (
                <SensorColumn key={idx}>
                  <SensorHeader>{sensor.type}</SensorHeader>
                  <Flex direction="row" gap={2} justify="center">
                    <SensorValue>
                      {isDisabled
                        ? '-'
                        : sensor.value !== null && sensor.value !== undefined
                          ? sensor.value
                          : '-'}
                    </SensorValue>
                    <SensorUnit>{isDisabled ? '' : sensor.unit}</SensorUnit>
                  </Flex>
                  <Tag
                    variant={tagVariant}
                    size="sm"
                    icon={<StatusIcon status={sensor.status} />}
                    {...(isDisabled
                      ? {
                          backgroundColor: theme.colors.surface.layout.overlay,
                          color: theme.colors.text.disabled,
                        }
                      : {})}
                  >
                    {sensor.statusLabel || sensor.status}
                  </Tag>
                  <SensorAdditionalInfo>{sensor.additionalInfo || '-'}</SensorAdditionalInfo>
                </SensorColumn>
              );
            })}
          </SensorTable>
        </SensorTableSection>
      </ContentGrid>
    </Card>
  );
};
